import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, Check } from 'lucide-react';
import Logo from './Logo';
import { useLang, LANGUAGES } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { t, lang, changeLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // On non-home pages the header always has a solid background
  const solid = scrolled || !isHome || open;
  const textDark = solid;

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/biens', label: t('nav.properties') },
    { to: '/expertises', label: t('nav.expertise') },
    { to: '/a-propos', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? 'bg-[#f4efe6]/95 backdrop-blur-md shadow-[0_1px_0_rgba(27,35,49,0.08)] py-4' : 'py-7'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" aria-label="STARKKE Immobilier">
          <Logo dark={!textDark} small={solid} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => {
            const active = l.to === '/' ? location.pathname === '/' : location.pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`link-underline font-sans-elegant text-[0.72rem] tracking-wide-2 uppercase transition-colors ${
                  textDark ? 'text-[#1b2331]' : 'text-[#f4efe6]'
                } ${active ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
              >
                {l.label}
              </Link>
            );
          })}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1.5 font-sans-elegant text-[0.72rem] tracking-wide-2 uppercase transition-colors outline-none ${
                  textDark ? 'text-[#1b2331]' : 'text-[#f4efe6]'
                }`}
              >
                <Globe className="w-4 h-4" />
                {LANGUAGES.find((l) => l.code === lang)?.short}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#f4efe6] border-[#1b2331]/15 min-w-[150px]">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  className="font-sans-elegant text-sm text-[#1b2331] cursor-pointer flex items-center justify-between focus:bg-[#eae2d3]"
                >
                  {l.label}
                  {lang === l.code && <Check className="w-3.5 h-3.5 text-[#b0894f]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <button
          className="lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? (
            <X className="w-6 h-6 text-[#1b2331]" />
          ) : (
            <Menu className={`w-6 h-6 ${textDark ? 'text-[#1b2331]' : 'text-[#f4efe6]'}`} />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5 bg-[#f4efe6]">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="font-sans-elegant text-sm tracking-wide-2 uppercase text-[#1b2331]"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-2 text-sm font-sans-elegant tracking-wide-2 text-[#1b2331]">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLang(l.code)}
                className={lang === l.code ? 'text-[#b0894f]' : 'opacity-50'}
              >
                {l.short}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
